import { IController } from 'angular';
import * as _ from 'lodash';
import { Cohort, ICohort } from '../../../../../../ng2/shared/constants/cohort.constant';
import { GraduationPlan, IGradPlanValue } from './../../../../../../ng2/shared/constants/graduation-plan.constant';

interface IGraduationPlanByHumanName {
  [humanName: string]: {
    readonly diplomaTypeCategory: string;
    readonly outcomeCategory: string;
  };
}

export class GradPlanningWizardTableCtrl implements IController {
  static inject: string[] = ['ObjectCache', '$scope', 'SDCStore', 'StudentListPreviewModal', '$state'];

  // class properties
  private hashGraduationPlanByHumanName: IGraduationPlanByHumanName;
  currentCohort: string;
  gradPlansByDiplomaTypeAndOutcome: any;
  plannedJuneGradRateCalculation: string;
  plannedAugGradRateCalculation: string;
  columnKeys: string[];
  filtersByGradPlan: any;
  dashPlaceholders: string[];

  constructor(
    private ObjectCache: any,
    private $scope,
    private SDCStore: any,
    public StudentListPreviewModal: any,
    private $state,
  ) {}

  $onInit(): void {
    this.hashGraduationPlanByHumanName = _.reduce(
      GraduationPlan,
      (acc, v: IGradPlanValue) => {
        acc[v.humanName] = {
          diplomaTypeCategory: v.diplomaTypeCategory,
          outcomeCategory: v.outcomeCategory, // can be null
        };
        return acc;
      },
      {},
    );

    // TODO: (NORMING) JC - We should only call setUi here once loading has completed
    this.$scope.$listenTo(this.SDCStore, ['ui', 'isLoading'], () => {
      const isLoading = this.SDCStore.getUiState().isLoading;
      if (!isLoading) {
        this.setUi();
        this.setListeners();
      }
    });
  }

  private setListeners() {
    this.$scope.$listenTo(this.SDCStore, ['school'], this.setUi.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['filteredFlattenedStudents'], this.setUi.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['filter'], this.setUi.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['flattenedStudents'], this.setUi.bind(this));
  }

  private setUi(): void {
    this.columnKeys = ['gradPlan', 'plannedDiplomaType', 'plannedGraduationDate'];
    this.currentCohort = this.getCurrentCohort();
    this.gradPlansByDiplomaTypeAndOutcome = this.getGradPlansByDiplomaTypeAndOutcome(this.currentCohort);

    const { plannedJuneGradRateCalculation, plannedAugGradRateCalculation } = this.getPlannedJuneAndAugGradRates(
      this.gradPlansByDiplomaTypeAndOutcome,
    );

    this.plannedJuneGradRateCalculation = plannedJuneGradRateCalculation;
    this.plannedAugGradRateCalculation = plannedAugGradRateCalculation;
    this.filtersByGradPlan = this.createFiltersForDiplomaTypes();
    this.dashPlaceholders = new Array(4);
  }

  private getCurrentCohort(): string {
    const currentCohortFilter: string[] = this.SDCStore.getFilter().cohort;
    const lastCohortInFilter: number = parseInt(_.last(currentCohortFilter));
    return `${lastCohortInFilter}`;
  }

  private getPlannedJuneAndAugGradRates(gradPlansByDiplomaTypeAndOutcome) {
    const plannedLocal4YearJune = gradPlansByDiplomaTypeAndOutcome['Planned Local']['4 Year June'].length;
    const plannedRegents4YearJune = gradPlansByDiplomaTypeAndOutcome['Planned Regents']['4 Year June'].length;
    const plannedAdvancedRegents4YearJune =
      gradPlansByDiplomaTypeAndOutcome['Planned Advanced Regents']['4 Year June'].length;
    const plannedLocal4YearAug = gradPlansByDiplomaTypeAndOutcome['Planned Local']['4 Year Aug'].length;
    const plannedRegents4YearAug = gradPlansByDiplomaTypeAndOutcome['Planned Regents']['4 Year Aug'].length;
    const plannedAdvancedRegents4YearAug =
      gradPlansByDiplomaTypeAndOutcome['Planned Advanced Regents']['4 Year Aug'].length;
    const graduates = gradPlansByDiplomaTypeAndOutcome.Graduates.Total.length;
    const total = gradPlansByDiplomaTypeAndOutcome.Total.Total.length;
    let plannedJuneGradRateCalculation;
    let plannedAugGradRateCalculation;

    if (total) {
      plannedJuneGradRateCalculation = (
        ((plannedLocal4YearJune + plannedRegents4YearJune + plannedAdvancedRegents4YearJune + graduates) / total) *
        100
      ).toFixed(1);

      plannedAugGradRateCalculation = (
        ((plannedLocal4YearJune +
          plannedRegents4YearJune +
          plannedAdvancedRegents4YearJune +
          plannedLocal4YearAug +
          plannedRegents4YearAug +
          plannedAdvancedRegents4YearAug +
          graduates) /
          total) *
        100
      ).toFixed(1);
    } else {
      plannedJuneGradRateCalculation = '0';
      plannedAugGradRateCalculation = '0';
    }

    return { plannedJuneGradRateCalculation, plannedAugGradRateCalculation };
  }

  private getGradPlansByDiplomaTypeAndOutcome(currentCohort): any {
    const filteredStudents = this.getFilteredStudents(currentCohort);

    const gradPlansByDiplomaTypeOutcome = _.reduce(
      filteredStudents,
      (acc: any, v: any) => {
        const gradPlanCategory = this.hashGraduationPlanByHumanName[v.gradPlan];

        if (!gradPlanCategory) {
          return acc;
        } else {
          if (gradPlanCategory.outcomeCategory) {
            // guard against counting nulls
            // TODO (FROM DS) THIS IS A HACK!!! WE NEED TO COMBINE 4 YEAR MARCH AND 4 YEAR JUNE IN A MORE ELEGANT WAY!!
            if (gradPlanCategory.outcomeCategory === '4 Year March') {
              acc[gradPlanCategory.diplomaTypeCategory]['4 Year June'].push(v.studentId);
              acc.Total['4 Year June'].push(v.studentId);
            } else {
              acc[gradPlanCategory.diplomaTypeCategory][gradPlanCategory.outcomeCategory].push(v.studentId);
              acc.Total[gradPlanCategory.outcomeCategory].push(v.studentId);
            }
          }

          if (gradPlanCategory) {
            // guard against a gradPlan not in GraduationPlan constant, such as "4 Year Jan"
            acc[gradPlanCategory.diplomaTypeCategory].Total.push(v.studentId);
            acc.Total.Total.push(v.studentId);
          }

          return acc;
        }
      },
      {
        'No Plan': {
          Total: [],
          '4 Year June': [],
          '4 Year Aug': [],
          '5 Year': [],
          '6+ Year': [],
        },
        'Planned Non-Grad': {
          Total: [],
          '4 Year June': [],
          '4 Year Aug': [],
          '5 Year': [],
          '6+ Year': [],
        },
        'Planned Local': {
          Total: [],
          '4 Year June': [],
          '4 Year Aug': [],
          '5 Year': [],
          '6+ Year': [],
        },
        'Planned Regents': {
          Total: [],
          '4 Year June': [],
          '4 Year Aug': [],
          '5 Year': [],
          '6+ Year': [],
        },
        'Planned Advanced Regents': {
          Total: [],
          '4 Year June': [],
          '4 Year Aug': [],
          '5 Year': [],
          '6+ Year': [],
        },
        'Negative Discharges': {
          Total: [],
          '4 Year June': [],
          '4 Year Aug': [],
          '5 Year': [],
          '6+ Year': [],
        },
        Graduates: {
          Total: [],
          '4 Year June': [],
          '4 Year Aug': [],
          '5 Year': [],
          '6+ Year': [],
        },
        Total: {
          Total: [],
          '4 Year June': [],
          '4 Year Aug': [],
          '5 Year': [],
          '6+ Year': [],
        },
      },
    );
    return gradPlansByDiplomaTypeOutcome;
  }

  private getFilteredStudents(currentCohort) {
    let filteredStudents = this.SDCStore.getFilteredFlattenedStudents();
    const cohort: ICohort = Cohort[currentCohort];

    if (cohort.studentYear === 4) {
      // do not show super seniors if cohort it 4
      filteredStudents = _.filter(filteredStudents, (s: any) => s.cohort === cohort.humanName);
    }

    return filteredStudents;
  }

  private createFiltersForDiplomaTypes() {
    const filterObj = {};
    _.forOwn(this.gradPlansByDiplomaTypeAndOutcome, (columns, row) => {
      filterObj[row] = {};
      _.forOwn(columns, (students, col) => {
        filterObj[row][col] = this.convertIdsToFilter(students);
      });
    });
    return filterObj;
  }

  public convertIdsToFilter(ids: string[]): string {
    return this.ObjectCache.cacheObject({ studentId: ids });
  }

  public openPreviewModal(filter) {
    this.StudentListPreviewModal.openPreview(filter, this.columnKeys, null);
  }

  public openGradGrid(studentIds: string[]): void {
    const filter = this.ObjectCache.cacheObject({ studentId: studentIds });
    this.$state.go('main.school.new-sdc.grad-planning.grid', {
      filter,
    });
  }

  public openSsv(studentIds: string[]): void {
    const filter = this.ObjectCache.cacheObject({ studentId: studentIds });
    this.$state.go('main.school.new-sdc.grad-planning.profile', {
      filter,
    });
  }
}
